<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { ref, computed, watch } from "vue"
import { useI18n } from "vue-i18n"
import type { CompanyFormData, CompanyType } from "@core/composables"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            company: {
                searchByName: "Par nom",
                searchBySiren: "Par SIREN",
                manualEntry: "Saisie manuelle",
                name: {
                    label: "Nom de l'organisation",
                },
                address: {
                    label: "Adresse",
                },
                type: {
                    label: "Type d'organisation",
                },
                idcompany: {
                    label: ({ named }: { named: (key: string) => string }) => {
                        const types: Record<string, string> = {
                            entreprise: "N° SIREN",
                            asso: "N° RNA ou SIREN",
                            copro: "N° d'immatriculation",
                            collectivite: "N° d'identification unique",
                        }
                        return `Identifiant (${types[named("type")] || "N°"})`
                    },
                    action: "Trouver le numéro",
                },
                legalForm: {
                    label: "Forme juridique",
                },
                readonly: {
                    message:
                        "Les champs ont été automatiquement remplis car l'organisation vient de vos invités. Vous ne pouvez pas les modifier.",
                },
                info: {
                    siren: "Rechercher par SIREN auprès de l'INSEE.",
                    name: "Rechercher par nom dans les organisations de vos invités ou auprès de l'INSEE.",
                    manual: "Saisir les informations manuellement.",
                },
            },
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
type SearchMode = "name" | "siren" | "manual"

interface SelectOption {
    label: string
    value: string
}

interface Props {
    /**
     * Données du formulaire (v-model)
     */
    modelValue: CompanyFormData
    /**
     * Options de types d'organisation
     */
    companyTypeOptions?: SelectOption[]
    /**
     * Options de formes juridiques
     */
    legalFormOptions?: SelectOption[]
    /**
     * Message d'erreur pour l'identifiant
     */
    companyIdError?: string
    /**
     * Force le mode de recherche
     */
    forceSearchMode?: SearchMode | null
}

interface Emits {
    /**
     * Émis lors de la mise à jour des données
     */
    (e: "update:modelValue", value: CompanyFormData): void
    /**
     * Émis lors du changement de type d'organisation
     */
    (e: "type-change"): void
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    companyTypeOptions: () => [],
    legalFormOptions: () => [],
    companyIdError: "",
    forceSearchMode: "name",
})

const emit = defineEmits<Emits>()

// ================================
// ÉTAT
// ================================

/**
 * Mode de recherche actuel
 */
const searchMode = ref<SearchMode>("name")

// ================================
// DONNÉES CALCULÉES
// ================================

/**
 * Options pour le sélecteur de mode de recherche
 */
const getSearchModeOptions = computed(() => [
    { label: t("company.searchByName"), value: "name" },
    { label: t("company.searchBySiren"), value: "siren" },
    { label: t("company.manualEntry"), value: "manual" },
])

/**
 * Indique si le formulaire est en lecture seule (entreprise existante)
 */
const getIsReadonly = computed(
    () => !!props.modelValue._id && searchMode.value !== "manual"
)

/**
 * Indique si on doit afficher le champ identifiant
 */
const getShowIdCompany = computed(() => {
    return (
        props.modelValue.type &&
        props.modelValue.type !== "collectivite" &&
        searchMode.value !== "siren"
    )
})

/**
 * Indique si on doit afficher le champ forme juridique
 */
const getShowLegalForm = computed(() => {
    return props.modelValue.type === "entreprise"
})

// ================================
// WATCHERS
// ================================

/**
 * Gère les changements de mode de recherche
 * Réinitialise le formulaire à chaque changement de mode.
 * En mode SIREN, on émet l'état en une seule fois : un emit puis updateField dans le même tick
 * réutiliserait encore l'ancien modelValue (parent pas synchronisé) et réinjecterait _id.
 */
watch(searchMode, (mode) => {
    if (mode === "siren") {
        emit("update:modelValue", {
            _id: undefined,
            name: "",
            address: null,
            idcompany: "",
            legalForm: "",
            type: "entreprise",
        } as CompanyFormData)
        return
    }
    resetForm()
})

/**
 * Force le mode de recherche
 */
watch(
    () => props.forceSearchMode,
    (mode: SearchMode | null) => {
        if (!mode) return
        searchMode.value = mode
    },
    {
        immediate: true,
    }
)
// ================================
// INTERACTIONS
// ================================

/**
 * Met à jour un champ du formulaire
 * @param field - Nom du champ
 * @param value - Nouvelle valeur
 */
function updateField<K extends keyof CompanyFormData>(
    field: K,
    value: CompanyFormData[K]
): void {
    emit("update:modelValue", {
        ...props.modelValue,
        [field]: value,
    })
}

/**
 * Applique le nouveau type en une seule émission : évite que le 2e updateField
 * utilise encore l'ancien modelValue (parent pas synchronisé) et réécrase le type.
 * @param newType - Nouveau type sélectionné
 */
function applyOrganizationTypeChange(newType: CompanyType | ""): void {
    const next: CompanyFormData = {
        ...props.modelValue,
        type: newType,
    }
    if (newType !== "entreprise") {
        next.legalForm = ""
    }
    if (newType === "collectivite") {
        next.idcompany = ""
    }
    emit("update:modelValue", next)
    emit("type-change")
}

/**
 * Ouvre le registre des copropriétés
 */
function onClickCoproRegistry(): void {
    window.open("https://www.registre-coproprietes.gouv.fr/annuaire", "_blank")
}

/**
 * Gère la mise à jour des données de l'entreprise depuis InputCompany
 * @param value - Données partielles de l'entreprise ou null (effacement → formulaire réinitialisé)
 */
function onUpdateCompany(value: Partial<CompanyFormData> | null): void {
    if (value === null) {
        resetForm()
        return
    }
    emit("update:modelValue", {
        ...props.modelValue,
        ...value,
    } as CompanyFormData)
}

/**
 * Réinitialise le formulaire aux valeurs par défaut
 */
function resetForm(): void {
    emit("update:modelValue", {
        _id: undefined,
        name: "",
        address: null,
        idcompany: "",
        legalForm: "",
        type: "",
    } as CompanyFormData)
}
</script>

<template>
    <div class="company-form flex flex-col gap-4">
        <!-- ================================ -->
        <!-- CHOIX DU MODE DE RECHERCHE -->
        <!-- ================================ -->
        <div class="flex justify-center">
            <BaseSelectButton
                v-model="searchMode"
                :options="getSearchModeOptions"
                option-label="label"
                option-value="value"
                :allow-empty="false"
            />
        </div>
        <BaseMessage
            v-if="searchMode === 'siren'"
            variant="simple"
            severity="secondary"
            size="small"
        >
            {{ t("company.info.siren") }}
        </BaseMessage>
        <BaseMessage
            v-if="searchMode === 'name'"
            variant="simple"
            severity="secondary"
            size="small"
        >
            {{ t("company.info.name") }}
        </BaseMessage>
        <BaseMessage
            v-if="searchMode === 'manual'"
            variant="simple"
            severity="secondary"
            size="small"
        >
            {{ t("company.info.manual") }}
        </BaseMessage>

        <!-- ================================ -->
        <!-- RECHERCHE PAR SIREN -->
        <!-- ================================ -->
        <InputCompanySiren
            v-if="searchMode === 'siren'"
            :model-value="modelValue"
            input-id="company-siren"
            :label="t('company.idcompany.label', { type: modelValue.type })"
            :error="companyIdError || undefined"
            :readonly="getIsReadonly && searchMode !== 'siren'"
            required
            @update:model-value="$emit('update:modelValue', $event)"
        />

        <!-- ================================ -->
        <!-- NOM DE L'ENTREPRISE -->
        <!-- ================================ -->
        <InputCompany
            :model-value="modelValue"
            input-id="company-name"
            :label="t('company.name.label')"
            :company-type="modelValue.type || 'entreprise'"
            :disable-search="searchMode !== 'name'"
            :disabled="getIsReadonly && searchMode !== 'name'"
            required
            @update:model-value="onUpdateCompany"
        />

        <!-- ================================ -->
        <!-- ADRESSE -->
        <!-- ================================ -->
        <InputAddress
            :model-value="modelValue.address"
            input-id="company-address"
            :label="t('company.address.label')"
            :readonly="getIsReadonly"
            :disabled="getIsReadonly"
            required
            @update:model-value="updateField('address', $event)"
        />

        <!-- ================================ -->
        <!-- TYPE D'ORGANISATION -->
        <!-- ================================ -->
        <BaseInputWrapper
            input-id="company-type"
            :label="t('company.type.label')"
            required
        >
            <BaseSelect
                :model-value="modelValue.type"
                input-id="company-type"
                :options="companyTypeOptions"
                option-label="label"
                option-value="value"
                :disabled="searchMode === 'siren' || getIsReadonly"
                fluid
                @update:model-value="applyOrganizationTypeChange"
            />
        </BaseInputWrapper>

        <!-- ================================ -->
        <!-- IDENTIFIANT -->
        <!-- ================================ -->
        <BaseInputWrapper
            v-if="getShowIdCompany"
            input-id="company-id"
            :label="t('company.idcompany.label', { type: modelValue.type })"
            :error="companyIdError || undefined"
            required
        >
            <div class="flex gap-2 w-full">
                <BaseInputText
                    id="company-id"
                    :model-value="modelValue.idcompany"
                    :readonly="getIsReadonly"
                    :disabled="getIsReadonly"
                    :invalid="!!companyIdError"
                    fluid
                    @update:model-value="updateField('idcompany', $event)"
                />
                <BaseButton
                    v-if="modelValue.type === 'copro'"
                    type="button"
                    severity="secondary"
                    size="small"
                    :label="t('company.idcompany.action')"
                    @click="onClickCoproRegistry"
                />
            </div>
        </BaseInputWrapper>

        <!-- ================================ -->
        <!-- FORME JURIDIQUE -->
        <!-- ================================ -->
        <BaseInputWrapper
            v-if="getShowLegalForm"
            input-id="company-legal-form"
            :label="t('company.legalForm.label')"
            required
        >
            <BaseSelect
                :model-value="modelValue.legalForm"
                input-id="company-legal-form"
                :options="legalFormOptions"
                option-label="label"
                option-value="value"
                :disabled="getIsReadonly"
                fluid
                @update:model-value="updateField('legalForm', $event)"
            />
        </BaseInputWrapper>

        <BaseMessage v-if="getIsReadonly" severity="info">
            <p class="text-sm">{{ t("company.readonly.message") }}</p>
        </BaseMessage>
    </div>
</template>
